/**
 * MarkdownEditor.jsx — v2.5.132
 * ─────────────────────────────────────────────────────────────────
 * Minimal markdown editor with a small toolbar (B / I / link /
 * H2). No dependencies — pure textarea + a row of buttons that
 * wrap or insert text at the current selection.
 *
 * Used by the Public Page card on LeagueSettingsPage for editing
 * the About content block.
 *
 * Why not a real markdown library / WYSIWYG?
 *   The spec called for "Markdown with a small toolbar (B/I/link/
 *   header)" — that's a 100-line component, not a 100-KB dep.
 *   Managers who know markdown type freely; those who don't can
 *   highlight + click. The render side (where the markdown becomes
 *   HTML for the public page) uses a real markdown library + a
 *   sanitizer; this component is just the input.
 */

import { useRef } from 'react';
import { Bold, Italic, Link as LinkIcon, Heading2 } from 'lucide-react';

export default function MarkdownEditor({ value, onChange, placeholder, disabled, rows = 10 }) {
  const textareaRef = useRef(null);

  // Helper: wrap the current selection in `before` + `after`,
  // OR insert `before + placeholder + after` if no selection.
  // After the edit, keep focus and select the placeholder so the
  // user can start typing immediately.
  function wrap(before, after, placeholderText = '') {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const selected = value.slice(start, end);
    const insert = selected || placeholderText;
    const next = value.slice(0, start) + before + insert + after + value.slice(end);
    onChange(next);
    // Re-focus + select the inserted region so the user keeps
    // momentum. requestAnimationFrame because the textarea's
    // value attribute updates on the next render cycle.
    requestAnimationFrame(() => {
      el.focus();
      const insertStart = start + before.length;
      el.setSelectionRange(insertStart, insertStart + insert.length);
    });
  }

  // For a header, the convention is "## " at the start of a line.
  // If the cursor is mid-line, push a newline before. If the
  // current line already starts with #, toggle it off.
  function toggleHeader() {
    const el = textareaRef.current;
    if (!el) return;
    const pos = el.selectionStart;
    // Find start of current line.
    const lineStart = value.lastIndexOf('\n', pos - 1) + 1;
    const lineEndIdx = value.indexOf('\n', pos);
    const lineEnd = lineEndIdx < 0 ? value.length : lineEndIdx;
    const line = value.slice(lineStart, lineEnd);
    let nextLine;
    if (line.startsWith('## ')) {
      // toggle off
      nextLine = line.slice(3);
    } else if (line.startsWith('# ')) {
      nextLine = '## ' + line.slice(2);
    } else {
      nextLine = '## ' + line;
    }
    const next = value.slice(0, lineStart) + nextLine + value.slice(lineEnd);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(lineStart, lineStart + nextLine.length);
    });
  }

  function insertLink() {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const selected = value.slice(start, end) || 'link text';
    // Insert: [selected](https://example.com). Select the URL
    // portion so the user types the actual URL immediately.
    const before = `[${selected}](`;
    const placeholderUrl = 'https://example.com';
    const after = ')';
    const next = value.slice(0, start) + before + placeholderUrl + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const urlStart = start + before.length;
      el.setSelectionRange(urlStart, urlStart + placeholderUrl.length);
    });
  }

  const btnStyle = {
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '4px 8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: 'var(--text-2)',
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: 12,
    opacity: disabled ? 0.5 : 1,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => wrap('**', '**', 'bold')} disabled={disabled} style={btnStyle} title="Bold (Ctrl+B)">
          <Bold size={12} />
        </button>
        <button type="button" onClick={() => wrap('_', '_', 'italic')} disabled={disabled} style={btnStyle} title="Italic">
          <Italic size={12} />
        </button>
        <button type="button" onClick={toggleHeader} disabled={disabled} style={btnStyle} title="Heading">
          <Heading2 size={12} />
        </button>
        <button type="button" onClick={insertLink} disabled={disabled} style={btnStyle} title="Link">
          <LinkIcon size={12} />
        </button>
      </div>
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        // Keyboard shortcut: Ctrl/Cmd+B for bold. Common pattern;
        // intercept here so the browser's "bookmark this page"
        // shortcut doesn't fire instead.
        onKeyDown={(e) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
            e.preventDefault();
            wrap('**', '**', 'bold');
          }
        }}
        style={{
          width: '100%',
          padding: 10,
          border: '1px solid var(--border)',
          borderRadius: 8,
          fontSize: 14,
          fontFamily: 'var(--font-mono)',
          background: 'var(--surface)',
          color: 'var(--text)',
          resize: 'vertical',
          minHeight: 120,
        }}
      />
    </div>
  );
}
